import { useEffect, useRef, useState } from "react";

// Detects when element enters viewport (once)
function useInView(threshold = 0.4) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (!ref.current) return;
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    obs.disconnect();
                }
            },
            { threshold },
        );
        obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

// Animated counter — handles numeric prefixes / suffixes and decimals
export default function CountUp({
    value,
    duration = 1600,
    prefix = "",
    suffix = "",
    decimals = 0,
    className = "",
    testId,
}) {
    const [ref, inView] = useInView();
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const start = performance.now();
        let raf;
        const tick = (t) => {
            const p = Math.min(1, (t - start) / duration);
            // easeOutExpo
            const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
            setDisplay(value * eased);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, value, duration]);

    const formatted = decimals
        ? display.toFixed(decimals).replace(".", ",")
        : Math.round(display).toString();

    return (
        <span ref={ref} data-testid={testId} className={className}>
            {prefix}
            {formatted}
            {suffix}
        </span>
    );
}
